import { autoUpdate, arrow, computePosition, ComputePositionReturn, flip, offset, shift, size, ReferenceElement } from '@floating-ui/dom';
import { Component, ComponentInterface, Element, Host, JSX, Listen, Prop, Watch, h, readTask } from '@stencil/core';
import { TooltipPosition, TooltipVariant } from '../../../components/overlays/tooltip/tooltip.types';
import { transformTagNames } from '../../../global/transform-tag-name';
import { Keys } from '../../../shared/keys';
import { getRandomId } from '../../../utils/get-random-id';
import { isTouchDevice } from '../../../utils/is-touch-device';
import { CURSOR_VARIANT, TOP_POSITION } from './tooltip.constants';
import { getDeepActiveElement } from '../../../shared/get-deep-active-element';
import { getFocusableElements } from '../../../shared/get-focusable-elements';

/**
 * Tooltips provide the user with object-related information.
 *
 * The Tooltip is rendered inside an overlay container to ensure it appears above other content and is not clipped by parent elements. This overlay container manages positioning and stacking context for the Tooltip. For instance, if the Tooltip is rendered within a modal, an element within the modal should be passed as the container using the `overlayContainer` prop.
 *
 * @slot - Element that triggers the tooltip like a button or anchor
 * @slot content - Content to be displayed in the tooltip overlay
 */
@Component({
  tag: 'wb-tooltip',
  styleUrl: './tooltip.scss',
  shadow: true,
})
export class TooltipComponent implements ComponentInterface {
  private uniqueId = `wb-tooltip-overlay-${getRandomId()}`;

  @Element()
  public el: HTMLElement;

  /** Optional tooltip variant (inline display property to use inside other webcomponents, if styling is broken) */
  @Prop({ reflect: true })
  public variant?: TooltipVariant;

  /** If present, shows overlay */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** Position of the tooltip */
  @Prop({ reflect: true })
  public position: TooltipPosition = TOP_POSITION;

  /** If present, won't trigger tooltip content */
  @Prop({ reflect: true, mutable: true })
  public disabled?: boolean = false;

  /** If present, the tooltip contents will be interactable */
  @Prop({ reflect: true })
  public interactable?: boolean = false;

  /** If present, will append tooltip's overlay to the container provided */
  @Prop() overlayContainer?: HTMLElement = document.body;

  @Watch('position')
  public onConfigurationChange(): void {
    this.updatePosition();
  }

  @Watch('open')
  public onOpenChange(): void {
    if (this.open && !this.disabled) {
      this.openOverlay(this.isTouchDevice);
    } else {
      this.closeOverlay();
    }
  }

  @Watch('disabled')
  public onDisableChange(): void {
    if (this.disabled && this.open) {
      this.closeOverlay();
    }
  }

  @Listen('keydown', { target: 'window' })
  public async onKeyDown(event: KeyboardEvent): Promise<void> {
    if (!this.open) {
      return;
    }

    if (event.key === Keys.ESC) {
      this.closeAndFocusTriggerElement();
    }

    if (this.interactable && event.key === Keys.TAB) {
      await this.handleTabNavigation(event);
    }
  }

  private TagNames = transformTagNames(this, 'wb-tooltip', ['wb-text', 'wb-link']);

  private overlayEl: HTMLElement;
  private overlayTextEl: HTMLElement;
  private triggerEl: HTMLElement;
  private virtualEl: ReferenceElement;
  private arrowEl: HTMLElement;
  private safePolygonEl: HTMLElement;
  private isTouchDevice: boolean;
  private skipNextTriggerFocus: boolean = false;
  private contentObserver: MutationObserver;
  private tooltipMaxWidth: string;

  private cleanup: () => void;

  public connectedCallback(): void {
    this.isTouchDevice = isTouchDevice();
  }

  public componentDidLoad(): void {
    if (this.open && !this.disabled) {
      // wait for the next tick to make sure the triggerEl is set
      readTask(() => this.openOverlay(false));
    }
  }

  public disconnectedCallback(): void {
    this.removeCloseOnClickOutsideListener();
    this.closeOverlay();
  }

  private updatePosition = async (): Promise<void> => {
    const computeElPosition = this.variant === CURSOR_VARIANT ? this.computeVirtualElPosition : this.computeTriggerElPosition;
    const { x, y, placement, middlewareData } = await computeElPosition();

    Object.assign(this.overlayEl.style, {
      left: `${x}px`,
      top: `${y}px`,
    });

    this.overlayEl.classList.forEach((className) => {
      // eslint-disable-next-line workbench/tag-name-transform
      if (className.startsWith('wb-tooltip--position-')) {
        this.overlayEl.classList.remove(className);
      }
    });
    this.overlayEl.classList.add(`wb-tooltip--position-${placement}`);

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]];

    if (this.interactable) {
      const SAFE_PADDING_SIDE = '10px';

      if (staticSide === 'top' || staticSide === 'bottom') {
        Object.assign(this.safePolygonEl.style, {
          left: '0',
          right: '',
          top: '',
          bottom: '',
          [staticSide]: `-${SAFE_PADDING_SIDE}`,
          height: SAFE_PADDING_SIDE,
          width: `${this.overlayEl.clientWidth}px`,
        });
      } else {
        Object.assign(this.safePolygonEl.style, {
          left: '',
          right: '',
          top: '0',
          bottom: '',
          [staticSide]: `-${SAFE_PADDING_SIDE}`,
          height: `${this.overlayEl.clientHeight}px`,
          width: SAFE_PADDING_SIDE,
        });
      }
    }

    if (middlewareData.arrow) {
      Object.assign(this.arrowEl.style, {
        display: undefined,
        left: middlewareData.arrow.x ? `${middlewareData.arrow.x}px` : '',
        top: middlewareData.arrow.y ? `${middlewareData.arrow.y}px` : '',
        right: '',
        bottom: '',
        [staticSide]: '-4px',
      });
    } else {
      this.arrowEl.style.display = 'none';
    }
  };

  /**
   * Callback for Floating UI 'size' middleware. Sets the tooltip's maxWidth to
   * the smaller of the available viewport space or the custom 'tooltipMaxWidth'
   * (if provided), ensuring the element never overflows its boundaries.
   */
  private applyMaxWidth = ({ availableWidth, elements }: { availableWidth: number; elements: { floating: HTMLElement } }): void => {
    Object.assign(elements.floating.style, {
      maxWidth: this.tooltipMaxWidth ? `min(${availableWidth}px, ${this.tooltipMaxWidth})` : `${availableWidth}px`,
    });
  };

  private computeVirtualElPosition = (): Promise<ComputePositionReturn> => {
    let preferedPlacement;
    let crossAxis = 12;

    switch (this.position) {
      case 'end':
        preferedPlacement = 'bottom-end';
        crossAxis = -crossAxis;
        break;
      default:
        preferedPlacement = 'bottom-start';
    }

    return computePosition(this.virtualEl, this.overlayEl, {
      placement: preferedPlacement,
      middleware: [
        offset({
          mainAxis: 24,
          crossAxis,
        }),
        // Keep overlay within container/viewport horizontally
        shift({
          padding: 8,
          boundary: this.overlayContainer,
        }),
        // Constrain width to available space to avoid overflow
        size({
          boundary: this.overlayContainer,
          padding: 8,
          apply: this.applyMaxWidth,
        }),
      ],
    });
  };

  private computeTriggerElPosition = (): Promise<ComputePositionReturn> => {
    let preferedPlacement;

    switch (this.position) {
      case 'top':
        preferedPlacement = 'top';
        break;
      case 'bottom':
        preferedPlacement = 'bottom';
        break;
      case 'start':
        preferedPlacement = 'left';
        break;
      case 'end':
        preferedPlacement = 'right';
        break;
    }

    return computePosition(this.triggerEl, this.overlayEl, {
      placement: preferedPlacement,
      strategy: 'absolute',
      middleware: [
        offset(8), //
        flip({
          padding: 16,
          boundary: this.overlayContainer,
        }),
        // Shift ensures the floating element stays within the boundary horizontally
        shift({
          padding: 8,
          boundary: this.overlayContainer,
        }),
        // Size constrains the tooltip width to the available space
        size({
          boundary: this.overlayContainer,
          padding: 8,
          apply: this.applyMaxWidth,
        }),
        arrow({ element: this.arrowEl, padding: 8 }),
      ],
    });
  };

  private openOverlay = async (closeOnClickOutside: boolean): Promise<void> => {
    if (document.getElementById(this.uniqueId)) {
      return; // make sure overlay is only attached once
    }

    if (closeOnClickOutside) {
      this.addCloseOnClickOutsideListener();
    }

    this.createOverlayEl();

    if (this.overlayEl) {
      this.overlayContainer.appendChild(this.overlayEl);
      this.updatePosition();
    }

    requestAnimationFrame(async () => {
      if (!this.open) {
        return;
      }

      if (this.overlayTextEl && typeof (this.overlayTextEl as any).componentOnReady === 'function') {
        await (this.overlayTextEl as any).componentOnReady();
      }

      const container = this.overlayEl?.querySelector(this.TagNames.WbText)?.shadowRoot?.querySelector('slot');
      if (container && this.interactable) {
        this.focusFirstInteractiveElement(container);
      }
    });
  };

  private closeOverlay = (): void => {
    if (this.isTouchDevice) {
      this.removeCloseOnClickOutsideListener();
    }
    // disconnect content observer
    this.contentObserver?.disconnect();

    if (this.overlayContainer.contains(this.overlayEl)) {
      this.overlayEl?.remove();
      this.arrowEl?.remove();
      this.cleanup();
    }
  };

  /** Focuses the first interactive element within the tooltip overlay, or the trigger element if there are no interactive elements. */
  private focusFirstInteractiveElement = async (container): Promise<void> => {
    const focusables = await getFocusableElements(container);

    if (focusables.length > 0) {
      focusables[0].focus();
    } else {
      this.triggerEl.shadowRoot.querySelector('button')?.focus();
    }
  };

  private addCloseOnClickOutsideListener = (): void => {
    window.addEventListener('touchstart', this.closeIfClickOutside);
  };

  private removeCloseOnClickOutsideListener = (): void => {
    window.removeEventListener('touchstart', this.closeIfClickOutside);
  };

  private closeIfClickOutside = (event: Event): void => {
    const path = event.composedPath();
    const isClickInside = path.includes(this.el) || (this.overlayEl && path.includes(this.overlayEl));

    if (!isClickInside) {
      this.closeOverlay();
    }
  };

  private updateTriggerEl = (event: Event): void => {
    const slottedElements = (event.target as HTMLSlotElement).assignedElements();

    if (slottedElements.length !== 1) {
      console.warn(`wb-tooltip: exactly one trigger element must be slotted, got ${slottedElements.length}`, event.target);
      if (!slottedElements.length) {
        return;
      }
    }

    this.triggerEl = slottedElements[0] as HTMLElement;

    this.triggerEl.addEventListener('mouseenter', () => {
      this.open = true;
    });
    this.triggerEl.addEventListener('mouseleave', (e: MouseEvent) => {
      if (!this.interactable || !this.overlayEl?.contains(e.relatedTarget as HTMLElement)) {
        this.open = false;
      }
    });
    this.triggerEl.addEventListener('focus', () => {
      if (this.skipNextTriggerFocus) {
        this.skipNextTriggerFocus = false;
        return;
      }
      this.open = true;
    });
    this.triggerEl.addEventListener('blur', () => {
      if (!this.interactable) {
        this.open = false;
      }
    });

    if (this.variant === CURSOR_VARIANT) {
      this.triggerEl.addEventListener('mousemove', this.setVirtualEl);
    }
  };

  private createOverlayEl() {
    if (!this.hasSlotContent() && !this.hasContent()) {
      return;
    }

    const tooltipEl = document.createElement('div');
    const arrowEl = document.createElement('div');

    tooltipEl.id = this.uniqueId;

    // eslint-disable-next-line workbench/tag-name-transform
    tooltipEl.classList.add('wb-tooltip-overlay');
    const tooltipElStyle = getComputedStyle(this.el);
    this.tooltipMaxWidth = tooltipElStyle.getPropertyValue('--tooltip-max-width');

    Object.assign(tooltipEl.style, {
      '--link-text-color': tooltipElStyle.getPropertyValue('--tooltip-text-color'),
      borderRadius: tooltipElStyle.getPropertyValue('--tooltip-border-radius'),
      backgroundColor: tooltipElStyle.getPropertyValue('--tooltip-background-color'),
      color: tooltipElStyle.getPropertyValue('--tooltip-text-color'),
      padding: `${tooltipElStyle.getPropertyValue('--tooltip-padding-top')} ${tooltipElStyle.getPropertyValue('--tooltip-padding-right')} ${tooltipElStyle.getPropertyValue('--tooltip-padding-bottom')} ${tooltipElStyle.getPropertyValue('--tooltip-padding-left')}`,
      maxWidth: tooltipElStyle.getPropertyValue('--tooltip-max-width'),
      position: 'absolute',
      zIndex: tooltipElStyle.getPropertyValue('--z-index'),
      boxSizing: 'border-box',
      pointerEvents: this.interactable ? 'auto' : 'none',
      width: 'max-content',
      top: '0', // add top and left value initially to prevent scroll bar flickering (https://git.i.mercedes-benz.com/dh-io-workbench/core/issues/3548)
      left: '0',
    });

    // eslint-disable-next-line workbench/tag-name-transform
    arrowEl.classList.add('wb-tooltip-overlay-arrow');
    Object.assign(arrowEl.style, {
      position: 'absolute',
      background: tooltipElStyle.getPropertyValue('--tooltip-background-color'),
      width: '8px',
      height: '8px',
      transform: 'rotate(45deg)',
    });

    const overlayTextEl = document.createElement(this.TagNames.WbText) as HTMLWbTextElement;
    overlayTextEl.size = 'm';
    overlayTextEl.static = true;

    const contentSlotClone = this.hasSlotContent()?.cloneNode(true) as HTMLElement;
    contentSlotClone?.removeAttribute('slot');
    overlayTextEl?.appendChild(contentSlotClone);

    tooltipEl?.appendChild(overlayTextEl);
    // keep reference for dynamic updates when slotted content changes
    this.overlayTextEl = overlayTextEl as HTMLElement;

    // observe host for changes to slotted content so overlay text updates live
    this.contentObserver?.disconnect();
    this.contentObserver = new MutationObserver(() => {
      if (!this.overlayTextEl) {
        return;
      }

      const newContent = this.hasSlotContent()?.cloneNode(true) as HTMLElement | null;
      if (!newContent) {
        return;
      }

      newContent.removeAttribute('slot');

      // replace children of overlayTextEl with new content
      while (this.overlayTextEl.firstChild) {
        this.overlayTextEl.removeChild(this.overlayTextEl.firstChild);
      }
      this.overlayTextEl.appendChild(newContent);

      // reposition overlay because size may have changed
      this.updatePosition();
    });

    this.contentObserver.observe(this.el, { childList: true, subtree: true, characterData: true });
    tooltipEl?.append(arrowEl);

    this.overlayEl = tooltipEl;
    this.arrowEl = arrowEl;

    if (this.interactable) {
      const safePolygonEl = document.createElement('div');

      // eslint-disable-next-line workbench/tag-name-transform
      safePolygonEl.classList.add('wb-tooltip-overlay-safe-polygon');
      Object.assign(safePolygonEl.style, {
        position: 'absolute',
        width: '0',
        height: '0',
        top: '0',
        left: '0',
      });

      tooltipEl?.append(safePolygonEl);

      this.safePolygonEl = safePolygonEl;

      const links: NodeListOf<HTMLElement> = this.overlayEl.querySelectorAll(`${this.TagNames.WbLink}, .wbx-link`);
      links.forEach(async (link) => {
        const isWebComponent = link.tagName.toLowerCase() === this.TagNames.WbLink.toLowerCase();

        if (isWebComponent && 'componentOnReady' in link) {
          await (link as any).componentOnReady();
        }

        const targetElement = isWebComponent ? link.shadowRoot?.querySelector('a') : link;

        const rawColor = tooltipElStyle.getPropertyValue('--tooltip-text-color') || tooltipElStyle.color || '';
        const colorValue = rawColor.trim();

        if (targetElement && colorValue) {
          targetElement.style.setProperty('color', colorValue);
        }

        if (colorValue) {
          link.style.setProperty('--tooltip-text-color', colorValue);
          link.style.setProperty('--link-text-color', colorValue);
        }
      });

      tooltipEl.addEventListener('mouseleave', (e: MouseEvent) => {
        if (e.relatedTarget !== this.triggerEl) {
          this.open = false;
        }
      });
    }

    this.cleanup = autoUpdate(this.triggerEl, this.overlayEl, this.updatePosition, { animationFrame: true });
  }

  private setVirtualEl = ({ clientX, clientY }) => {
    this.virtualEl = {
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: clientX,
          y: clientY,
          left: clientX,
          right: clientX,
          top: clientY,
          bottom: clientY,
        };
      },
    };

    this.updatePosition();
  };

  /**
   * Handles Tab and Shift+Tab key navigation to trap focus within the overlay.
   * Closes the overlay and returns focus to the trigger element when navigating out.
   */
  private async handleTabNavigation(event: KeyboardEvent): Promise<void> {
    const container = this.overlayEl?.querySelector(this.TagNames.WbText)?.shadowRoot?.querySelector('slot');
    const focusables = await getFocusableElements(container);
    const currentActiveElement = getDeepActiveElement() as HTMLElement | null;
    const firstFocusable = focusables[0];
    const lastFocusable = focusables[focusables.length - 1];

    if (event.shiftKey && currentActiveElement === firstFocusable) {
      event.preventDefault();
      this.closeAndFocusTriggerElement();
    } else if (!event.shiftKey && currentActiveElement === lastFocusable) {
      event.preventDefault();
      this.closeAndFocusTriggerElement();
    }
  }

  /**
   * Closes the overlay and shifts focus back to the trigger button.
   */
  private closeAndFocusTriggerElement(): void {
    this.open = false;
    this.skipNextTriggerFocus = true;
    if (this.interactable) {
      this.triggerEl.shadowRoot.querySelector('button')?.focus();
    }
  }

  private hasSlotContent = (): HTMLElement | null => {
    return this.el.querySelector('[slot="content"]');
  };

  private getSlotContent = (): string => {
    return this.hasSlotContent()?.innerText.trim() ?? '';
  };

  private hasContent = (): boolean => {
    return !!this.getSlotContent();
  };

  public render(): JSX.Element {
    return (
      <Host class="wb-tooltip" role="tooltip" aria-label={this.getSlotContent()}>
        <div class="wb-tooltip__container" data-testid="wb-tooltip__container">
          <slot onSlotchange={this.updateTriggerEl} />
        </div>
      </Host>
    );
  }
}
